import { ClientContext } from '../core/ClientContextFactory';
import { ClientTokenIntent } from '../core/ClientTokenHandler';
import { SuccessCallbackOptions } from '../core/ProgressNotifier';
import { PrimerClientError } from '../errors';
import {
  CheckoutOptions,
  ErrorCode,
  PaymentMethodToken,
  ResumeToken,
  SinglePaymentMethodCheckoutOptions,
} from '../types';
import { getSuccessScene } from './SuccessSceneUtils';
import { IViewUtils } from './ui/types';

interface TokenizeSuccessCallback<T = void> {
  (data: PaymentMethodToken, options?: SuccessCallbackOptions): T;
}

interface ResumeSuccessCallback<T = void> {
  (data: ResumeToken, options?: SuccessCallbackOptions): T;
}

type CallbackReturnValue = undefined | true | { clientToken: string };

///////////////////////////////////////////
// Payment
///////////////////////////////////////////

const handlePromise = (
  promise: undefined | void | Promise<CallbackReturnValue>,
  context: ClientContext,
  viewUtils: IViewUtils,
  options: CheckoutOptions | SinglePaymentMethodCheckoutOptions,
  callOptions: SuccessCallbackOptions | undefined,
  intentHandlers: IntentHandlers | undefined,
  errorHandler?: ErrorHandler,
) => {
  const success = (returnValue: CallbackReturnValue) => {
    /* Show success screen */
    if (!returnValue || returnValue === true) {
      const successScene = getSuccessScene(
        options as CheckoutOptions | SinglePaymentMethodCheckoutOptions,
        callOptions?.successSceneHandler,
      );

      if (successScene) {
        viewUtils.store.setScene(successScene);
      }
      viewUtils.store.setIsLoading(false);
      viewUtils.store.setIsProcessing(false);

      return;
    }

    /* Refresh client token */
    if (typeof returnValue === 'object' && 'clientToken' in returnValue) {
      const newClientToken = returnValue.clientToken;

      // Same token
      if (
        newClientToken === context.clientTokenHandler.getCurrentClientToken()
      ) {
        errorHandler?.(
          PrimerClientError.fromErrorCode(ErrorCode.RESUME_ERROR, {
            message: 'Same client token provided',
          }),
        );
        return;
      }

      // Client token issue
      try {
        context.clientTokenHandler.setClientToken(returnValue.clientToken);
      } catch (e) {
        errorHandler?.(
          PrimerClientError.fromErrorCode(ErrorCode.RESUME_ERROR, {
            message: 'Cannot decode client token',
          }),
        );
        return;
      }
      const decodedClientToken = context.clientTokenHandler.getCurrentDecodedClientToken();
      if (!decodedClientToken) {
        errorHandler?.(
          PrimerClientError.fromErrorCode(ErrorCode.RESUME_ERROR, {
            message: 'Cannot decode client token',
          }),
        );
        return;
      }

      // Cannot process intent
      const intent = decodedClientToken?.intent ?? '';
      const intentHandler = intentHandlers?.[intent];

      if (!intentHandler) {
        errorHandler?.(
          PrimerClientError.fromErrorCode(ErrorCode.RESUME_ERROR, {
            message: `Cannot do step ${intentHandler}`,
          }),
        );
        return;
      }

      intentHandler();
    }
  };

  if (promise && promise.then) {
    promise.then(success).catch((error: Error) => {
      viewUtils.store.setErrorMessage(error.message);
      viewUtils.store.setIsLoading(false);
      viewUtils.store.setIsProcessing(false);
      viewUtils.store.setCurrentToken(null);
    });
  } else {
    success(undefined);
  }
};

type IntentHandlers = {
  [ClientTokenIntent.THREEDS_AUTHENTICATION]?: () => void;
};

type ErrorHandler = (e: PrimerClientError) => void;

export function createTokenizeSuccessCallback(
  fn: TokenizeSuccessCallback<void | Promise<CallbackReturnValue>>,
  context: ClientContext,
  viewUtils: IViewUtils,
  options: CheckoutOptions | SinglePaymentMethodCheckoutOptions,
  intentHandlers?: IntentHandlers,
  errorHandler?: ErrorHandler,
): TokenizeSuccessCallback {
  return (data: PaymentMethodToken, callOptions?: SuccessCallbackOptions) => {
    viewUtils.store.setCurrentToken(data);
    const promise = fn(data);
    handlePromise(
      promise,
      context,
      viewUtils,
      options,
      callOptions,
      intentHandlers,
      errorHandler,
    );
  };
}

export function createResumeSuccessCallback(
  fn: ResumeSuccessCallback<void | Promise<CallbackReturnValue>>,
  context: ClientContext,
  viewUtils: IViewUtils,
  options: CheckoutOptions | SinglePaymentMethodCheckoutOptions,
  intentHandlers?: IntentHandlers,
  errorHandler?: ErrorHandler,
): ResumeSuccessCallback {
  return (data: ResumeToken, callOptions?: SuccessCallbackOptions) => {
    const promise = fn(data);
    handlePromise(
      promise,
      context,
      viewUtils,
      options,
      callOptions,
      intentHandlers,
      errorHandler,
    );
  };
}

///////////////////////////////////////////
// Vault
///////////////////////////////////////////

const handleVaultPromise = (
  promise: undefined | void | Promise<CallbackReturnValue>,
  viewUtils: IViewUtils,
) => {
  //TODO(at): No success scene in vault manager
  const success = () => {
    viewUtils.store.setIsLoading(false);
    viewUtils.store.setIsProcessing(false);
  };

  if (promise && promise.then) {
    promise.then(success).catch((error: Error) => {
      viewUtils.store.setErrorMessage(error.message);
      viewUtils.store.setIsLoading(false);
      viewUtils.store.setIsProcessing(false);
      viewUtils.store.setCurrentToken(null);
    });
  } else {
    success();
  }
};

export function createVaultTokenizeSuccessCallback(
  fn: TokenizeSuccessCallback<void | Promise<CallbackReturnValue>>,
  viewUtils: IViewUtils,
) {
  return (data: PaymentMethodToken) => {
    const promise = fn(data);
    handleVaultPromise(promise, viewUtils);
  };
}
